import React from 'react';
import './search-result-card.less';

import CheckIcon from '@atlaskit/icon/glyph/check';
import CommentIcon from '@atlaskit/icon/glyph/comment';
import LikeIcon from '@atlaskit/icon/glyph/like';
import analytics from '../../utils/analytics';
import { Breadcrumb, TitleSection, TicketType } from '.';

export interface SearchResultCardProps {
    ticketType?: TicketType;
    breadcrumbs?: string[];
    title: string;
    description: string;
    dateModified?: string;
    answerAccepted?: boolean;
    views?: number;
    comments?: number;
    markedUseful?: number;
    href: string;
    platform?: string;
}

export const SearchResultCard: React.FC<SearchResultCardProps> = ({
    breadcrumbs,
    title,
    description,
    dateModified,
    answerAccepted,
    views,
    comments,
    markedUseful,
    ticketType,
    href,
    platform
}: SearchResultCardProps) => {
    const openLinkInNewWindow = () => {
        if (href !== undefined && href !== '') {
            analytics.trackLinkClick(href, title);
            window.open(href, '_blank');
        }
    };

    const isFooterVisible = answerAccepted || views || comments || markedUseful;

    const formattedDate = dateModified
        ? new Date(dateModified).toLocaleDateString()
        : undefined;

    const renderAnswerAccepted = () => {
        if (answerAccepted) {
            return (
                <span
                    className="answer-accepted background-success"
                    data-testid="answer-accepted"
                >
                    <span className="check-icon">
                        <CheckIcon size="small" label="Answer accepted" />
                    </span>
                    <span>Answer Accepted</span>
                </span>
            );
        }
        return null;
    };

    const renderViews = () => {
        if (views !== undefined) {
            return <span data-testid="views">{views} views</span>;
        }
        return null;
    };

    const renderComments = () => {
        if (comments !== undefined) {
            return (
                <span className="comments" data-testid="comments">
                    <CommentIcon size="small" label="Comments" />
                    <span>{comments}</span>
                </span>
            );
        }
        return null;
    };

    const renderMarkedUseful = () => {
        if (markedUseful !== undefined) {
            return (
                <span className="marked-useful" data-testid="marked-useful">
                    <LikeIcon size="small" label="marked-useful" />
                    <span>{markedUseful}</span>
                </span>
            );
        }
        return null;
    };

    return (
        <button
            className="search-result-card search-result-card__card-button-wrapper"
            data-testid="search-result-card"
            onClick={openLinkInNewWindow}
        >
            <div className="search-result-card__card-container">
                <div className="left-section">
                    <Breadcrumb breadcrumbs={breadcrumbs} />
                    <div className="card-content text-gray">
                        <TitleSection ticketType={ticketType} title={title} />
                        <p className="card-description">{description}</p>
                        {formattedDate && (
                            <p
                                className="font-weight-600"
                                data-testid="datemodified"
                            >
                                {formattedDate}
                            </p>
                        )}
                        {isFooterVisible && (
                            <div
                                className="card-footer"
                                data-testid="card-footer"
                            >
                                {renderAnswerAccepted()}
                                {renderViews()}
                                {renderComments()}
                                {renderMarkedUseful()}
                            </div>
                        )}
                    </div>
                </div>
                <div className="right-section text-gray">
                    <span className="deployment font-weight-600">
                        {platform}
                    </span>
                </div>
            </div>
        </button>
    );
};
